import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCustomerTickets,
  useCustomerPortalLookups,
  useCustomerTicketStats,
  useCustomerInsights,
  useCustomerRecentActivity,
  customerPortalQueryKeys,
  type CustomerTicketFilterParams,
} from "../../hooks/useCustomerPortal";
import { useCustomerPortalSignalR } from "../../contexts/CustomerPortalSignalRContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  Filter,
  SortAsc,
  SortDesc,
  Calendar,
  MessageCircle,
  Paperclip,
  AlertTriangle,
  Ticket as TicketIcon,
  User,
  Folder,
  Clock,
  CheckCircle,
  AlertCircle,
  Hourglass,
  XCircle,
  PauseCircle,
  ArrowDown,
  Minus,
  ArrowUp,
  X,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  CheckCircle2,
  CircleDot,
  Users,
} from "lucide-react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDateFormat } from "../../hooks/useDateFormat";
import {
  getStatusString,
  getPriorityString,
  getStatusAccentColor,
  getPriorityBackgroundTint,
} from "../utils/ticketUtils";
import { TicketStatusColors, PriorityColors, type CustomerTicketScope } from "../../types/tickets";
import { cn } from "@/lib/utils";
import { CustomerPortalTicketsSkeleton } from "../../components/LoadingSkeleton";
import type { OrganizationInfo } from "../../lib/portal-loaders";
import { useAuth } from "../../hooks/useAuth";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import TechSupportAgent from "../../assets/TechSupportAgent.png";
import { TicketProgressBar } from "../../components/TicketProgressBar";
import { StatsCard } from "../components/StatsCard";
import { TicketDistributionChart } from "../components/TicketDistributionChart";
import { ActivityTimeline } from "../components/ActivityTimeline";
import { InsightsCharts } from "../components/InsightsCharts";
import { DashboardTabs } from "../components/DashboardTabs";
import { usePersistedFilters } from "../../hooks/usePersistedFilters";
import { showToast } from "../../lib/toast";

interface CustomerPortalTicketsProps {
  organizationInfo?: OrganizationInfo;
  organizationId?: string | null;
}

interface MultiSelectFilterProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  primaryColor: string;
  secondaryColor: string;
  /** Prevents deselecting the last item (for organization filters) */
  requireAtLeastOne?: boolean;
  /** Show Select All / Clear buttons in dropdown */
  showSelectAllControls?: boolean;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  primaryColor,
  secondaryColor,
  requireAtLeastOne,
  showSelectAllControls,
}) => {
  const [open, setOpen] = useState(false);
  const hasSelection = selectedValues.length > 0;
  const allSelected = selectedValues.length === options.length && options.length > 0;

  // Track pointer movement to distinguish scrolls from taps on mobile
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const pointerMovedRef = useRef(false);
  const MOVEMENT_THRESHOLD = 10; // pixels

  const handleOpenChange = (newOpen: boolean) => {
    // Only allow closing through Radix's handler (e.g., clicking outside, pressing Escape)
    // Opening is handled manually via onClick to prevent scroll-triggered opens
    if (!newOpen) {
      setOpen(false);
    }
  };

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      // Prevent removing last item if required
      if (requireAtLeastOne && selectedValues.length === 1) {
        return;
      }
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectAll = () => onChange(options.map((o) => o.value));

  const clearAll = () => {
    if (requireAtLeastOne) {
      // Keep first option selected
      onChange([options[0]?.value].filter(Boolean));
    } else {
      onChange([]);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full md:w-[200px] justify-between transition-all duration-200"
          style={{
            borderColor: hasSelection ? secondaryColor : undefined,
            borderWidth: hasSelection ? "2px" : "1px",
            backgroundColor: hasSelection ? secondaryColor + "10" : undefined,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = secondaryColor;
            e.currentTarget.style.borderWidth = "2px";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = hasSelection
              ? secondaryColor
              : "";
            e.currentTarget.style.borderWidth = hasSelection ? "2px" : "1px";
          }}
          onPointerDownCapture={(e) => {
            // Intercept pointerdown before Radix gets it to prevent auto-open on touch
            pointerStartRef.current = { x: e.clientX, y: e.clientY };
            pointerMovedRef.current = false;
            e.stopPropagation();
          }}
          onPointerMove={(e) => {
            // Track if user is scrolling (moved finger significantly)
            if (pointerStartRef.current) {
              const dx = Math.abs(e.clientX - pointerStartRef.current.x);
              const dy = Math.abs(e.clientY - pointerStartRef.current.y);
              if (dx > MOVEMENT_THRESHOLD || dy > MOVEMENT_THRESHOLD) {
                pointerMovedRef.current = true;
              }
            }
          }}
          onPointerUp={() => {
            pointerStartRef.current = null;
          }}
          onPointerCancel={() => {
            pointerStartRef.current = null;
            pointerMovedRef.current = false;
          }}
          onClick={() => {
            // Only toggle if user didn't scroll (onClick fires after successful tap)
            if (!pointerMovedRef.current) {
              setOpen((prev) => !prev);
            }
            pointerMovedRef.current = false;
          }}
        >
          <div className="flex items-center">
            <Filter
              className="mr-2 h-4 w-4"
              style={{
                color: hasSelection ? secondaryColor : primaryColor,
              }}
            />
            <span>
              {allSelected ? `All ${label}` : label}
              {hasSelection && !allSelected && ` (${selectedValues.length})`}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[200px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        {showSelectAllControls && (
          <>
            <div className="flex items-center justify-between px-2 py-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  selectAll();
                }}
                disabled={allSelected}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
                disabled={requireAtLeastOne && selectedValues.length <= 1}
              >
                Clear
              </Button>
            </div>
          </>
        )}
        <DropdownMenuSeparator />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selectedValues.includes(option.value)}
            onCheckedChange={() => toggleValue(option.value)}
            onSelect={(e) => e.preventDefault()}
            disabled={requireAtLeastOne && selectedValues.length === 1 && selectedValues.includes(option.value)}
            className={cn(
              "cursor-pointer",
              requireAtLeastOne && selectedValues.length === 1 && selectedValues.includes(option.value) && "opacity-50 cursor-not-allowed"
            )}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};


export const CustomerPortalTickets: React.FC<CustomerPortalTicketsProps> = ({
  organizationInfo,
}) => {
  const navigate = useNavigate();
  const { customer } = useAuth();
  // Determine URL pattern for navigation
  const isPathBased = window.location.pathname.startsWith("/org/");

  // Default filter values
  const defaultFilters = {
    search: '',
    status: [] as string[],
    priority: [] as string[],
    sort: 'desc' as 'asc' | 'desc',
    dashboard: false,
    tab: 'overview' as 'overview' | 'activity' | 'insights',
    scope: 'mine' as CustomerTicketScope,
    page: 1,
    pageSize: 20,
  };

  // Get URL search params for filter persistence
  const urlParams = useSearch({ from: '/org/$orgSlug/portal/tickets/' });

  // Use organization-specific storage key to prevent filter bleed across orgs
  const storageKey = `customer-portal-tickets-filters-${organizationInfo?.organizationId ?? 'unknown'}`;

  const { updateFilters } = usePersistedFilters(
    storageKey,
    defaultFilters,
    urlParams
  );

  // Derive state from URL params (persisted)
  const searchQuery = urlParams.search ?? '';
  const statusFilter = urlParams.status ?? [];
  const priorityFilter = urlParams.priority ?? [];
  const sortOrder = urlParams.sort ?? 'desc';
  const isDashboardExpanded = urlParams.dashboard ?? false;
  const dashboardTab = urlParams.tab ?? 'overview';
  const currentPage = urlParams.page ?? 1;
  const pageSize = urlParams.pageSize ?? 20;
  // My requests (tickets the customer opened) or Participating (opened by someone else who added them as a participant)
  const ticketScope: CustomerTicketScope = urlParams.scope ?? 'mine';
  const isMyRequests = ticketScope === 'mine';

  // Track previous filter values to detect changes and reset page
  const prevFiltersRef = useRef({ search: searchQuery, status: statusFilter, priority: priorityFilter, scope: ticketScope });

  // Reset page to 1 when filters change (but not when page/pageSize change)
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.search !== searchQuery ||
      JSON.stringify(prevFilters.status) !== JSON.stringify(statusFilter) ||
      JSON.stringify(prevFilters.priority) !== JSON.stringify(priorityFilter) ||
      prevFilters.scope !== ticketScope;

    if (filtersChanged && currentPage !== 1) {
      updateFilters({ page: 1 });
    }

    prevFiltersRef.current = { search: searchQuery, status: statusFilter, priority: priorityFilter, scope: ticketScope };
  }, [searchQuery, statusFilter, priorityFilter, ticketScope, currentPage, updateFilters]);

  // Mappings for chart segment names to filter values
  const statusNameToFilter: Record<string, string> = {
    'Open': '1',
    'In Progress': '2',
    'Pending': '3',
    'Resolved': '4',
    'Closed': '5',
    'On Hold': '6',
  };

  const priorityNameToFilter: Record<string, string> = {
    'Low': '1',
    'Medium': '2',
    'High': '3',
    'Critical': '4',
  };

  // The stats and charts describe the customer's own requests, so filtering from them shows My requests
  const filterMyRequests = useCallback((filters: { status?: string[]; priority?: string[] }) => {
    updateFilters({ scope: 'mine', status: filters.status ?? [], priority: filters.priority ?? [] });
  }, [updateFilters]);

  // Click handlers for chart segments and cards
  const handleStatusChartClick = useCallback((name: string) => {
    const filterValue = statusNameToFilter[name];
    if (filterValue) {
      filterMyRequests({ status: [filterValue] });
    }
  }, [filterMyRequests, statusNameToFilter]);

  const handlePriorityChartClick = useCallback((name: string) => {
    const filterValue = priorityNameToFilter[name];
    if (filterValue) {
      filterMyRequests({ priority: [filterValue] });
    }
  }, [filterMyRequests, priorityNameToFilter]);

  const handleTotalTicketsClick = useCallback(() => {
    filterMyRequests({});
  }, [filterMyRequests]);

  const handleResolvedClick = useCallback(() => {
    filterMyRequests({ status: ['4', '5'] });  // 4 = Resolved, 5 = Closed
  }, [filterMyRequests]);

  // Determine active segments for chart highlighting
  const activeStatusSegment = useMemo(() => {
    if (isMyRequests && statusFilter.length === 1) {
      return Object.entries(statusNameToFilter).find(([, v]) => v === statusFilter[0])?.[0] || null;
    }
    return null;
  }, [isMyRequests, statusFilter, statusNameToFilter]);

  const activePrioritySegment = useMemo(() => {
    if (isMyRequests && priorityFilter.length === 1) {
      return Object.entries(priorityNameToFilter).find(([, v]) => v === priorityFilter[0])?.[0] || null;
    }
    return null;
  }, [isMyRequests, priorityFilter, priorityNameToFilter]);

  // Build filter params for API call
  const filterParams: CustomerTicketFilterParams = useMemo(() => ({
    page: currentPage,
    pageSize: pageSize,
    search: searchQuery || undefined,
    status: statusFilter.length > 0 ? statusFilter.map(s => parseInt(s, 10)) : undefined,
    priority: priorityFilter.length > 0 ? priorityFilter.map(p => parseInt(p, 10)) : undefined,
    sortOrder: sortOrder,
    scope: ticketScope,
  }), [currentPage, pageSize, searchQuery, statusFilter, priorityFilter, sortOrder, ticketScope]);

  const { data: ticketsData, isLoading, isFetching, error } = useCustomerTickets(filterParams);
  const { data: lookups } = useCustomerPortalLookups();
  const { data: stats } = useCustomerTicketStats();
  const { data: insights, isLoading: insightsLoading } = useCustomerInsights();
  const { data: recentActivity, isLoading: activityLoading } = useCustomerRecentActivity();
  const { formatDateTime } = useDateFormat();

  // SignalR real-time updates
  const queryClient = useQueryClient();
  const { signalR, isConnected } = useCustomerPortalSignalR();

  // Subscribe to SignalR events for real-time ticket list updates
  useEffect(() => {
    if (!isConnected) return;

    const handleTicketUpdated = () => {
      // Show toast notification
      showToast.info('Ticket Updated', 'One of your tickets has been updated');
      // Invalidate ticket list and stats to refresh with updated data
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.stats(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.activity(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.insights(),
        });
      }, 100);
    };

    const handleCommentAdded = () => {
      // Show toast notification
      showToast.info('New Comment', 'A new comment was added to one of your tickets');
      // Refresh ticket list to show updated comment counts
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.activity(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.insights(),
        });
      }, 100);
    };

    const handleParticipantsChanged = () => {
      // Refresh ticket list when participants change
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.stats(),
        });
      }, 100);
    };

    const handleCommentAttachmentAdded = () => {
      // Refresh ticket list when attachments are added
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
      }, 100);
    };

    const handleTicketCreated = () => {
      // Show toast notification
      showToast.success('Ticket Created', 'Your new ticket has been submitted');
      // Refresh all dashboard data when a new ticket is created
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.stats(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.activity(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.insights(),
        });
      }, 100);
    };

    const handleTicketActivity = () => {
      // Show toast notification
      showToast.info('Ticket Activity', 'There is new activity on one of your tickets');
      // Refresh activity and lists when ticket activity occurs (status changes, etc.)
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.lists(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.activity(),
        });
        queryClient.invalidateQueries({
          queryKey: customerPortalQueryKeys.tickets.insights(),
        });
      }, 100);
    };

    // Register event handlers and store cleanup functions
    const cleanupTicketUpdated = signalR.on('TicketUpdated', handleTicketUpdated);
    const cleanupCommentAdded = signalR.on('CommentAdded', handleCommentAdded);
    const cleanupParticipantsChanged = signalR.on('ParticipantsChanged', handleParticipantsChanged);
    const cleanupCommentAttachmentAdded = signalR.on('CommentAttachmentAdded', handleCommentAttachmentAdded);
    const cleanupTicketCreated = signalR.on('TicketCreated', handleTicketCreated);
    const cleanupTicketActivity = signalR.on('TicketActivity', handleTicketActivity);

    // Cleanup - only removes this component's handlers, not global handlers
    return () => {
      cleanupTicketUpdated();
      cleanupCommentAdded();
      cleanupParticipantsChanged();
      cleanupCommentAttachmentAdded();
      cleanupTicketCreated();
      cleanupTicketActivity();
    };
  }, [isConnected, signalR, queryClient]);

  // Organization branding
  const {
    getThemedPrimaryColor,
    getThemedSecondaryColor,
    getThemedPrimaryWithOpacity,
    getContrastText,
  } = usePortalBranding();

  const tickets = ticketsData?.tickets || [];
  const hasActiveFilters = searchQuery !== "" || statusFilter.length > 0 || priorityFilter.length > 0;
  const totalCount = ticketsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Handle page size change - reset to page 1
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    updateFilters({ pageSize: newPageSize, page: 1 });
  }, [updateFilters]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    updateFilters({ page: newPage });
  }, [updateFilters]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleSearchInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = getThemedPrimaryColor();
    e.currentTarget.style.borderWidth = "2px";
    e.currentTarget.style.boxShadow = `0 0 0 3px ${getThemedPrimaryWithOpacity(0.15)}`;
  }, [getThemedPrimaryColor, getThemedPrimaryWithOpacity]);

  const handleSearchInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.borderColor = "";
    e.currentTarget.style.borderWidth = "";
    e.currentTarget.style.boxShadow = "";
  }, []);

  const handleClearSearch = useCallback(() => {
    updateFilters({ search: '' });
  }, [updateFilters]);

  // Create a filter key that changes when any filter changes
  // This will force re-animation of ticket cards when filters are applied
  const filterKey = useMemo(() => {
    return `${ticketScope}-${searchQuery}-${statusFilter.join(',')}-${priorityFilter.join(',')}-${sortOrder}-${currentPage}-${pageSize}`;
  }, [ticketScope, searchQuery, statusFilter, priorityFilter, sortOrder, currentPage, pageSize]);

  // Server handles filtering and sorting now - tickets are already filtered/sorted from API

  // Prepare ticket distribution data
  const ticketDistributionData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: 'Open', value: stats.openTickets, color: '#3b82f6' },
      { name: 'In Progress', value: stats.inProgressTickets, color: '#eab308' },
      { name: 'Pending', value: stats.pendingTickets, color: '#f97316' },
      { name: 'Resolved', value: stats.resolvedTickets, color: '#22c55e' },
    ];
  }, [stats]);

  // Prepare priority distribution data from stats (not filtered tickets)
  const priorityDistributionData = useMemo(() => {
    if (!stats) return [];
    return [
      { name: 'Low', value: stats.lowPriorityTickets, color: '#22c55e' },
      { name: 'Medium', value: stats.mediumPriorityTickets, color: '#eab308' },
      { name: 'High', value: stats.highPriorityTickets, color: '#f97316' },
      { name: 'Critical', value: stats.criticalPriorityTickets, color: '#ef4444' },
    ];
  }, [stats]);

  const handleCreateTicket = () => {
    if (isPathBased && organizationInfo) {
      navigate({
        to: "/org/$orgSlug/portal/submit-ticket",
        params: {
          orgSlug: organizationInfo.subdomain,
        },
      });
    } else {
      // Legacy portal route removed - this should not be used anymore
      console.warn(
        "Legacy portal navigation attempted - please use org-based routes"
      );
    }
  };

  const handleViewTicket = (ticketId: string) => {
    if (isPathBased && organizationInfo) {
      navigate({
        to: "/org/$orgSlug/portal/tickets/$ticketId",
        params: {
          orgSlug: organizationInfo.subdomain,
          ticketId: ticketId,
        },
      });
    } else {
      // Legacy portal route removed - this should not be used anymore
      console.warn(
        "Legacy portal navigation attempted - please use org-based routes"
      );
    }
  };

  const toggleSort = () => {
    updateFilters({ sort: sortOrder === "desc" ? "asc" : "desc" });
  };

  if (error) {
    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Welcome Header */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 flex-1">
                <img
                  src={TechSupportAgent}
                  alt="Tech Support Agent"
                  className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 object-contain flex-shrink-0"
                />
                <div className="text-center sm:text-left">
                  <h1
                    className="text-xl sm:text-2xl md:text-3xl font-bold"
                    style={{ color: getThemedPrimaryColor() }}
                  >
                    Hi {customer?.firstName || "there"}! Here's what we're working on for you today.
                  </h1>
                </div>
              </div>
              <Button
                size="lg"
                onClick={handleCreateTicket}
                className="flex-shrink-0 transition-all duration-300 hover:scale-105 font-semibold px-6 py-5"
                style={{
                  backgroundColor: getThemedPrimaryColor(),
                  color: getContrastText(getThemedPrimaryColor()),
                  boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                  e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                  e.currentTarget.style.boxShadow = `0 15px 25px -5px ${getThemedPrimaryWithOpacity(0.5)}`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                  e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                  e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}`;
                }}
              >
                <Plus className="mr-2 h-5 w-5" />
                Create Ticket
              </Button>
            </div>
          </CardContent>
        </Card>

        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Unable to load your tickets. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return <CustomerPortalTicketsSkeleton />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-3 sm:gap-4 flex-1">
              <img
                src={TechSupportAgent}
                alt="Tech Support Agent"
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 object-contain flex-shrink-0"
              />
              <div className="text-center sm:text-left">
                <h1
                  className="text-xl sm:text-2xl md:text-3xl font-bold"
                  style={{ color: getThemedPrimaryColor() }}
                >
                  Hi {customer?.firstName || "there"}! Here's what we're working on for you today.
                </h1>
              </div>
            </div>
            <Button
              size="lg"
              onClick={handleCreateTicket}
              className="flex-shrink-0 transition-all duration-300 hover:scale-105 font-semibold px-6 py-5"
              style={{
                backgroundColor: getThemedPrimaryColor(),
                color: getContrastText(getThemedPrimaryColor()),
                boxShadow: `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                e.currentTarget.style.boxShadow = `0 15px 25px -5px ${getThemedPrimaryWithOpacity(0.5)}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                e.currentTarget.style.boxShadow = `0 10px 15px -3px ${getThemedPrimaryWithOpacity(0.3)}`;
              }}
            >
              <Plus className="mr-2 h-5 w-5" />
              Create Ticket
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard Section */}
      {stats && (
        <div className="space-y-4">
          {/* Stats Cards Grid - Always Visible */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatsCard
              label="Open"
              value={stats.openTickets}
              icon={CircleDot}
              colorClass="text-blue-600"
              bgColorClass="bg-blue-100 dark:bg-blue-900/30"
              borderColorClass="border-blue-600"
              onClick={() => filterMyRequests({ status: ["1"] })}
              isActive={isMyRequests && statusFilter.length === 1 && statusFilter[0] === "1"}
              isMinimized={!isDashboardExpanded}
            />
            <StatsCard
              label="Working On It"
              value={stats.inProgressTickets}
              icon={Hourglass}
              colorClass="text-yellow-600"
              bgColorClass="bg-yellow-100 dark:bg-yellow-900/30"
              borderColorClass="border-yellow-600"
              onClick={() => filterMyRequests({ status: ["2"] })}
              isActive={isMyRequests && statusFilter.length === 1 && statusFilter[0] === "2"}
              isMinimized={!isDashboardExpanded}
            />
            <StatsCard
              label="Waiting For You"
              value={stats.pendingTickets}
              icon={AlertCircle}
              colorClass="text-orange-600"
              bgColorClass="bg-orange-100 dark:bg-orange-900/30"
              borderColorClass="border-orange-600"
              onClick={() => filterMyRequests({ status: ["3"] })}
              isActive={isMyRequests && statusFilter.length === 1 && statusFilter[0] === "3"}
              isMinimized={!isDashboardExpanded}
            />
            <StatsCard
              label="Avg Resolution"
              value={stats.averageResolutionTimeDays > 0
                ? `${stats.averageResolutionTimeDays.toFixed(1)}d`
                : "N/A"}
              icon={Clock}
              colorClass="text-green-600"
              bgColorClass="bg-green-100 dark:bg-green-900/30"
              borderColorClass="border-green-600"
              isMinimized={!isDashboardExpanded}
              infoTooltip="Average time to resolve your tickets, based on tickets resolved in the last 30 days."
            />
          </div>

          {/* Expand/Collapse Button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateFilters({ dashboard: !isDashboardExpanded })}
              className="transition-all duration-200 hover:scale-105"
            >
              {isDashboardExpanded ? (
                <>
                  <ChevronUp className="mr-2 h-4 w-4" />
                  Hide Dashboard Details
                </>
              ) : (
                <>
                  <ChevronDown className="mr-2 h-4 w-4" />
                  Show Dashboard Details
                </>
              )}
            </Button>
          </div>

          {/* Expandable Dashboard Content */}
          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden ${
              isDashboardExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <DashboardTabs
              activeTab={dashboardTab}
              onTabChange={(tab) => updateFilters({ tab: tab as 'overview' | 'activity' | 'insights' })}
              overviewContent={
                <>
                  {/* Stats Card */}
                  <Card className="mb-4">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-base font-semibold">General Statistics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Total Tickets - Clickable */}
                        <div
                          className={cn(
                            "flex items-center gap-4 p-4 rounded-lg border-l-4 border-purple-500 bg-gray-50 dark:bg-gray-900/50 transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5",
                            isMyRequests && statusFilter.length === 0 && priorityFilter.length === 0 && "ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-900"
                          )}
                          onClick={handleTotalTicketsClick}
                        >
                          <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30">
                            <TicketIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-gray-900 dark:text-gray-50">
                              {stats.totalTickets}
                            </p>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              Total Tickets
                            </p>
                          </div>
                        </div>

                        {/* Resolved - Clickable */}
                        <div
                          className={cn(
                            "flex items-center gap-4 p-4 rounded-lg border-l-4 border-green-500 bg-gray-50 dark:bg-gray-900/50 transition-all duration-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5",
                            isMyRequests && statusFilter.length === 2 && statusFilter.includes("4") && statusFilter.includes("5") && "ring-2 ring-green-500 ring-offset-2 dark:ring-offset-gray-900"
                          )}
                          onClick={handleResolvedClick}
                        >
                          <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                            <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-gray-900 dark:text-gray-50">
                              {stats.resolvedTickets}
                            </p>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              Resolved
                            </p>
                          </div>
                        </div>

                        {/* Resolution Rate */}
                        <div className="flex items-center gap-4 p-4 rounded-lg border-l-4 border-blue-500 bg-gray-50 dark:bg-gray-900/50">
                          <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                            <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-gray-900 dark:text-gray-50">
                              {stats.totalTickets > 0
                                ? `${((stats.resolvedTickets / stats.totalTickets) * 100).toFixed(0)}%`
                                : 'N/A'}
                            </p>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              Resolution Rate
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <TicketDistributionChart
                      data={ticketDistributionData}
                      title="Status Distribution"
                      onSegmentClick={handleStatusChartClick}
                      activeSegment={activeStatusSegment}
                    />
                    <TicketDistributionChart
                      data={priorityDistributionData}
                      title="Priority Distribution"
                      onSegmentClick={handlePriorityChartClick}
                      activeSegment={activePrioritySegment}
                    />
                  </div>
                </>
              }
              activityContent={
                <ActivityTimeline
                  activities={recentActivity || []}
                  isLoading={activityLoading}
                  onActivityClick={handleViewTicket}
                />
              }
              insightsContent={
                <InsightsCharts
                  responseTrendData={insights?.responseTimeTrend || []}
                  ticketVolumeTrend={insights?.ticketVolumeTrend || []}
                  resolutionRateTrend={insights?.resolutionRateTrend || []}
                  isLoading={insightsLoading}
                />
              }
            />
          </div>
        </div>
      )}

      {/* Ticket Management Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TicketIcon className="h-5 w-5" />
            Manage Tickets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* My requests / Participating - tickets the customer opened, or is a participant on */}
          <Tabs
            value={ticketScope}
            onValueChange={(value) => updateFilters({ scope: value as CustomerTicketScope })}
          >
            <TabsList className="grid w-full grid-cols-2 sm:inline-grid sm:w-auto">
              <TabsTrigger value="mine" className="flex items-center gap-2">
                <TicketIcon className="h-4 w-4" />
                My requests
              </TabsTrigger>
              <TabsTrigger value="participating" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Participating
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Search */}
            <div className="relative w-full md:max-w-md">
              <div
                className="absolute left-2.5 top-1/2 transform -translate-y-1/2 rounded-full p-1.5"
                style={{
                  backgroundColor: getThemedPrimaryWithOpacity(0.15),
                }}
              >
                <Search
                  className="h-3.5 w-3.5"
                  style={{ color: getThemedPrimaryColor() }}
                />
              </div>
              <Input
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => updateFilters({ search: e.target.value })}
                className="pl-11 pr-9 transition-all duration-200"
                onFocus={handleSearchInputFocus}
                onBlur={handleSearchInputBlur}
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 rounded-full p-1.5 transition-all duration-200 hover:scale-110"
                  style={{
                    backgroundColor: getThemedPrimaryWithOpacity(0.15),
                    color: getThemedPrimaryColor(),
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getThemedSecondaryColor();
                    e.currentTarget.style.color = getContrastText(
                      getThemedSecondaryColor()
                    );
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getThemedPrimaryWithOpacity(0.15);
                    e.currentTarget.style.color = getThemedPrimaryColor();
                  }}
                  aria-label="Clear search"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Status Filter and Sort on same row */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <MultiSelectFilter
                label="Status"
                options={
                  lookups?.statuses.map((status) => ({
                    value: status.value.toString(),
                    label: status.label,
                  })) || []
                }
                selectedValues={statusFilter}
                onChange={(values) => updateFilters({ status: values })}
                primaryColor={getThemedPrimaryColor()}
                secondaryColor={getThemedSecondaryColor()}
                showSelectAllControls={true}
              />

              {/* Priority Filter */}
              <MultiSelectFilter
                label="Priority"
                options={
                  lookups?.priorities.map((priority) => ({
                    value: priority.value.toString(),
                    label: priority.label,
                  })) || []
                }
                selectedValues={priorityFilter}
                onChange={(values) => updateFilters({ priority: values })}
                primaryColor={getThemedPrimaryColor()}
                secondaryColor={getThemedSecondaryColor()}
                showSelectAllControls={true}
              />

              {/* Sort */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggleSort}
                className="shrink-0 transition-all duration-200"
                style={{
                  borderColor: getThemedSecondaryColor(),
                  borderWidth: "2px",
                  backgroundColor: getThemedSecondaryColor() + "10",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    getThemedSecondaryColor();
                  e.currentTarget.style.color = getContrastText(
                    getThemedSecondaryColor()
                  );
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    getThemedSecondaryColor() + "10";
                  e.currentTarget.style.color = "";
                }}
              >
                {sortOrder === "desc" ? (
                  <SortDesc
                    className="h-4 w-4"
                    style={{ color: getThemedSecondaryColor() }}
                  />
                ) : (
                  <SortAsc
                    className="h-4 w-4"
                    style={{ color: getThemedSecondaryColor() }}
                  />
                )}
              </Button>

              {/* Clear Filters */}
              {(searchQuery !== "" ||
                statusFilter.length > 0 ||
                priorityFilter.length > 0) && (
                <Button
                  variant="outline"
                  onClick={() => updateFilters({ search: '', status: [], priority: [] })}
                  className="w-full sm:w-auto transition-all duration-200"
                  style={{
                    borderColor: getThemedSecondaryColor(),
                    borderWidth: "2px",
                    backgroundColor: getThemedSecondaryColor() + "10",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getThemedSecondaryColor();
                    e.currentTarget.style.color = getContrastText(
                      getThemedSecondaryColor()
                    );
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getThemedSecondaryColor() + "10";
                    e.currentTarget.style.color = "";
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 dark:border-gray-700 my-4" />

          {/* Tickets List */}
          {tickets.length === 0 ? (
          <div className="p-4 text-center">
            {isMyRequests ? (
              <TicketIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            ) : (
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            )}
            {!hasActiveFilters && !isMyRequests ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No tickets you&apos;re participating in
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  When someone adds you as a participant on their request, it shows up here so you can follow it and reply.
                </p>
              </>
            ) : !hasActiveFilters ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No tickets yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Create your first support ticket to get help from our team.
                </p>
                <Button onClick={handleCreateTicket}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Ticket
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No tickets match your search
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Try adjusting your search criteria or filters.
                </p>
                <Button
                  variant="outline"
                  onClick={() => updateFilters({ search: '', status: [], priority: [] })}
                >
                  Clear Filters
                </Button>
              </>
            )}
          </div>
        ) : (
        <div className="space-y-4">
          {tickets.map((ticket, index) => {
            // Helper function to get status icon component
            const getStatusIcon = (status: number) => {
              const statusStr = getStatusString(status).toLowerCase();
              switch (statusStr) {
                case "open":
                  return TicketIcon;
                case "inprogress":
                  return Hourglass;
                case "pending":
                  return AlertCircle;
                case "resolved":
                  return CheckCircle;
                case "closed":
                  return XCircle;
                case "onhold":
                  return PauseCircle;
                default:
                  return TicketIcon;
              }
            };

            // Helper function to get priority icon component
            const getPriorityIcon = (priority: number) => {
              const priorityStr = getPriorityString(priority).toLowerCase();
              switch (priorityStr) {
                case "low":
                  return ArrowDown;
                case "medium":
                  return Minus;
                case "high":
                  return ArrowUp;
                case "critical":
                  return AlertTriangle;
                default:
                  return Minus;
              }
            };

            const StatusIcon = getStatusIcon(ticket.status);
            const PriorityIcon = getPriorityIcon(ticket.priority);

            return (
              <Card
                key={`${ticket.id}-${filterKey}`}
                style={{
                  animationDelay: index < 12 ? `${index * 50}ms` : "0ms",
                }}
                className={`group cursor-pointer hover:shadow-2xl dark:hover:shadow-gray-400/60 hover:-translate-y-2 hover:scale-[1.02] transition-all duration-300 animate-slide-fade-in border-l-4 ${getStatusAccentColor(ticket.status)} ${getPriorityBackgroundTint(ticket.priority)}`}
                onClick={() => handleViewTicket(ticket.id)}
              >
                <CardContent className="p-0">
                  {/* Header Section */}
                  <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between gap-6">
                      {/* Left side: Title and Badges */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-tight mb-2">
                          <span className="text-blue-600 dark:text-blue-400 font-mono">
                            #{ticket.ticketNumber}
                          </span>
                          <span className="mx-2 text-gray-400 dark:text-gray-500">
                            —
                          </span>
                          {ticket.subject}
                        </h3>

                        {/* Status and Priority Badges */}
                        <div className="flex flex-wrap gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md flex-shrink-0 ${TicketStatusColors[ticket.status]}`}
                          >
                            <StatusIcon className="h-3.5 w-3.5" />
                            {getStatusString(ticket.status)}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md flex-shrink-0 ${PriorityColors[ticket.priority]}`}
                          >
                            <PriorityIcon className="h-3.5 w-3.5" />
                            {getPriorityString(ticket.priority)}
                          </span>
                          {ticket.isOwner === false && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md flex-shrink-0 bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                              <Users className="h-3.5 w-3.5" />
                              Requested by {ticket.customer?.fullName || "another customer"}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Right side: Progress Bar */}
                      <div className="flex-shrink-0 hidden sm:flex">
                        <TicketProgressBar currentStatus={ticket.status} size="sm" />
                      </div>
                    </div>
                  </div>

                  {/* Mobile Progress Bar Section - shown only on mobile */}
                  <div className="sm:hidden px-3 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-center">
                    <TicketProgressBar currentStatus={ticket.status} size="sm" />
                  </div>

                  {/* Expandable Section - Description and Metadata Footer */}
                  <div className="overflow-hidden transition-all duration-300 ease-in-out max-h-[600px] opacity-100 md:max-h-0 md:opacity-0 md:group-hover:max-h-[600px] md:group-hover:opacity-100">
                    {/* Description Section */}
                    {ticket.description && (
                      <div className="px-3 sm:px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-200 line-clamp-2 leading-relaxed">
                          {ticket.description}
                        </p>
                      </div>
                    )}

                    {/* Metadata Footer - Grid Layout */}
                    <div className="p-3 sm:p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                      {/* Assigned To */}
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-purple-50 dark:bg-purple-900/40 rounded-md border border-transparent dark:border-purple-800/50">
                          <User className="h-3.5 w-3.5 text-purple-600 dark:text-purple-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Assigned To
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                            {ticket.assignedTo?.fullName || "Unassigned"}
                          </p>
                        </div>
                      </div>

                      {/* Created Date */}
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-green-50 dark:bg-green-900/40 rounded-md border border-transparent dark:border-green-800/50">
                          <Calendar className="h-3.5 w-3.5 text-green-600 dark:text-green-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Created
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                            {formatDateTime(ticket.createdAt)}
                          </p>
                        </div>
                      </div>

                      {/* Updated Date */}
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-orange-50 dark:bg-orange-900/40 rounded-md border border-transparent dark:border-orange-800/50">
                          <Clock className="h-3.5 w-3.5 text-orange-600 dark:text-orange-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Updated
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                            {formatDateTime(ticket.updatedAt)}
                          </p>
                        </div>
                      </div>

                      {/* Category */}
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/40 rounded-md border border-transparent dark:border-indigo-800/50">
                          <Folder className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                            Category
                          </p>
                          <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                            {ticket.category?.path || ticket.category?.name || "No Category"}
                          </p>
                        </div>
                      </div>

                      {/* Resolved Date (conditional) */}
                      {ticket.resolvedAt && (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-green-50 dark:bg-green-900/40 rounded-md border border-transparent dark:border-green-800/50">
                            <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-300" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                              Resolved
                            </p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                              {formatDateTime(ticket.resolvedAt)}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Comments (only show if they exist) */}
                      {ticket.commentCount > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-teal-50 dark:bg-teal-900/40 rounded-md border border-transparent dark:border-teal-800/50">
                            <MessageCircle className="h-3.5 w-3.5 text-teal-600 dark:text-teal-300" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                              Comments
                            </p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                              {ticket.commentCount}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Attachments (only show if they exist) */}
                      {ticket.attachmentCount > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 bg-pink-50 dark:bg-pink-900/40 rounded-md border border-transparent dark:border-pink-800/50">
                            <Paperclip className="h-3.5 w-3.5 text-pink-600 dark:text-pink-300" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                              Files
                            </p>
                            <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                              {ticket.attachmentCount}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalCount > 0 && (
        <div className="bg-background px-3 sm:px-6 py-3 sm:py-4 border-t border-border rounded-b-lg mt-4">
          {totalPages > 1 ? (
            <div className="flex items-center justify-between">
              {/* Mobile pagination - stacked layout */}
              <div className="flex flex-col gap-3 sm:hidden w-full">
                {/* Page size and record count */}
                <div className="flex flex-col gap-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <label className="text-xs text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-xs border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-xs text-foreground">per page</span>
                  </div>
                  <p className="text-xs text-foreground">
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{" "}
                    <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span> of{" "}
                    <span className="font-medium">{totalCount}</span> results
                  </p>
                </div>

                {/* Pagination buttons */}
                <div className="flex justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages || isFetching}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>

              {/* Desktop pagination */}
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-foreground">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-foreground">per page</span>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">
                      Showing{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, totalCount)}
                      </span>{" "}
                      of <span className="font-medium">{totalCount}</span> results
                    </p>
                  </div>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || isFetching}
                      className="rounded-l-md rounded-r-none"
                    >
                      Previous
                    </Button>

                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNumber =
                        Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <Button
                          key={pageNumber}
                          variant={pageNumber === currentPage ? "default" : "outline"}
                          onClick={() => handlePageChange(pageNumber)}
                          disabled={isFetching}
                          className="rounded-none"
                        >
                          {pageNumber}
                        </Button>
                      );
                    })}

                    <Button
                      variant="outline"
                      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages || isFetching}
                      className="rounded-r-md rounded-l-none"
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              </div>
            </div>
          ) : (
            // Show record count and page size selector even when there's only one page
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 w-full sm:w-auto">
                <div className="flex items-center gap-2">
                  <label className="text-xs sm:text-sm text-foreground">Show:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 text-xs sm:text-sm border border-input rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs sm:text-sm text-foreground">per page</span>
                </div>
                <p className="text-xs sm:text-sm text-foreground text-center sm:text-left">
                  Showing{" "}
                  <span className="font-medium">
                    {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
            </div>
          )}
        </div>
      )}
        </CardContent>
      </Card>
    </div>
  );
};
